import { createContext, type ReactNode, useEffect, useRef } from "react";

const mediaQueries = {
  Mobile: "(max-width: 767px)",
  Tablet: "(min-width: 768px) and (max-width: 991px)",
  Desktop: "(min-width: 992px)",
} as const;
export type MediaStore = Record<`is${keyof typeof mediaQueries}` | "hoverNone" | "isMobileApp", boolean>;
type MediaCtx = {
  getStore(): MediaStore;
  subscribe: (arg: () => void) => () => void;
};

export const MediaContext = createContext<null | MediaCtx>(null);

// used to detect if it is a touch device
const detectTouchDevice = () => {
  if (typeof window === "undefined") return false;
  return navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches || window.matchMedia("(hover: none)").matches;
};

// used to detect if any page is opened in mobile app webview
const detectMobileAppWebView = () => {
  if (typeof window === "undefined") return false;

  const agent = navigator.userAgent.toLowerCase();

  const isAndroidWebView = agent.includes("android") && agent.includes("wv");

  const isIOSWebView = (agent.includes("iphone") || agent.includes("ipad")) && !agent.includes("safari");

  return isAndroidWebView || isIOSWebView;
};

type MQKey = keyof typeof mediaQueries;

function initStore(arg: Partial<MediaStore>): MediaStore {
  const store = { ...arg } as MediaStore;

  (Object.keys(mediaQueries) as MQKey[]).forEach((key) => {
    store[`is${key}`] = typeof window !== "undefined" ? window.matchMedia(mediaQueries[key]).matches : false;
  });

  return store;
}

const initialState: MediaStore = initStore({
  hoverNone: detectTouchDevice(),
  isMobileApp: detectMobileAppWebView(),
});

const MediaProvider = ({ children }: { children: ReactNode }) => {
  const store = useRef<MediaStore>(initialState);
  const subscribers = useRef(new Set<() => void>());

  const getStore = () => store.current;
  const setStore = (key: `is${string}`, value: boolean) => {
    store.current = { ...getStore(), [key]: value };
    subscribers.current.forEach((callback: () => void) => callback());
  };
  const subscribe = (callback: () => void) => {
    subscribers.current.add(callback);
    return () => subscribers.current.delete(callback);
  };

  useEffect(() => {
    const controller = new AbortController();

    for (const [bool, media] of Object.entries(mediaQueries)) {
      const query = window.matchMedia(media);
      setStore(`is${bool}`, query.matches);
      query.addEventListener(
        "change",
        () => {
          setStore(`is${bool}`, query.matches);
        },
        { signal: controller.signal },
      );
    }

    return () => controller.abort();
  }, []);

  // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop -- it's ok
  return <MediaContext.Provider value={{ getStore, subscribe }}>{children}</MediaContext.Provider>;
};
export default MediaProvider;
